import { addMonths, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { Platform, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { withUnistyles } from 'react-native-unistyles';

import { Box, Button, Icon, Text } from '@squire/ui';

import { useScreenWidth } from '@/hooks/shared/useScreenWidth';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { DateFormat, formatDate } from '@/utils/dateUtils';

import { CalendarMonth } from './CalendarMonth';
import { getCalendarLayout } from './calendarLayout';
import { styles } from './styles';
import type { CalendarProps } from './types';
import { useCalendarAnimations } from './useCalendarAnimations';

const AnimatedFlatList = withUnistyles(Animated.FlatList);

const Calendar = (props: CalendarProps) => {
  const {
    selectedDate,
    currentMonth = selectedDate || new Date(),
    minDate,
    maxDate,
    disabledDates = [],
    waitlistOnlyDates = [],
    onDateSelect,
    onMonthChange,
    horizontalScroll = true,
    testID,
    /* istanbul ignore next - platform-specific defaults */
    monthsAhead = Platform.select({ web: 4, default: 2 }),
    /* istanbul ignore next - platform-specific defaults */
    monthsBehind = Platform.select({ web: 4, default: 2 }),
    ...viewProps
  } = props;

  const { t, i18n } = useScreenTranslations('Calendar');
  const screenWidth = useScreenWidth();

  const initialCurrentMonth = useRef(currentMonth).current;

  // The FlatList is narrower than the screen (parent padding), so its width is
  // measured on layout; seeded with screenWidth until then.
  const [pageWidth, setPageWidth] = useState(screenWidth);
  // Guards the one-time initial positioning below.
  const hasPositionedRef = useRef(false);

  const months = useMemo(() => {
    if (!horizontalScroll) return [initialCurrentMonth];

    const monthsArray: Date[] = [];
    for (let i = -monthsBehind; i <= monthsAhead; i++) {
      monthsArray.push(addMonths(initialCurrentMonth, i));
    }
    return monthsArray;
  }, [initialCurrentMonth, horizontalScroll, monthsAhead, monthsBehind]);

  const { monthWidth, monthGap, monthsPerPage, monthStride, trailingPadding } =
    getCalendarLayout({ listWidth: pageWidth, screenWidth });
  const lastPageIndex = Math.max(0, months.length - monthsPerPage);

  const { scrollViewRef, scrollHandler, scrolledMonthIndex, commitMonthIndex } =
    useCalendarAnimations({
      months,
      monthsBehind,
      onMonthChange,
      monthStride,
    });

  // Record the measured width and jump (no animation) to the month on screen,
  // using that width so the page lands exactly on a boundary. With several
  // months per page the opening or current month may have too few months after
  // it to fill a page, so it is clamped to the last full page.
  const handleListLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width } = event.nativeEvent.layout;
      if (width <= 0) return;
      setPageWidth((prev) => (prev === width ? prev : width));

      const isFirstLayout = !hasPositionedRef.current;
      if (!isFirstLayout && width === pageWidth) return;
      hasPositionedRef.current = true;

      const layout = getCalendarLayout({ listWidth: width, screenWidth });
      const index = Math.min(
        isFirstLayout ? monthsBehind : scrolledMonthIndex,
        Math.max(0, months.length - layout.monthsPerPage),
      );
      if (index !== scrolledMonthIndex) commitMonthIndex(index);
      scrollViewRef.current?.scrollToOffset({
        offset: index * layout.monthStride,
        animated: false,
      });
    },
    [
      monthsBehind,
      screenWidth,
      scrollViewRef,
      pageWidth,
      scrolledMonthIndex,
      months.length,
      commitMonthIndex,
    ],
  );

  const scrollToMonth = useCallback(
    (index: number) => {
      /* istanbul ignore next - defensive guard, buttons are disabled at boundaries */
      if (index >= 0 && index <= lastPageIndex) {
        // Set the title to the destination month up front; the scroll animates
        // there next.
        commitMonthIndex(index);

        // The FlatList's imperative offset API works on web and native iOS
        // through the unistyles wrapper; Reanimated's worklet scrollTo does not.
        scrollViewRef.current?.scrollToOffset({
          offset: index * monthStride,
          animated: true,
        });
      }
    },
    [lastPageIndex, scrollViewRef, monthStride, commitMonthIndex],
  );

  const handlePreviousMonth = useCallback(() => {
    scrollToMonth(scrolledMonthIndex - 1);
  }, [scrolledMonthIndex, scrollToMonth]);

  const handleNextMonth = useCallback(() => {
    scrollToMonth(scrolledMonthIndex + 1);
  }, [scrolledMonthIndex, scrollToMonth]);

  const isDateDisabled = (date: Date): boolean => {
    const dayStart = startOfDay(date);

    if (
      minDate &&
      !isSameDay(dayStart, startOfDay(minDate)) &&
      isBefore(dayStart, startOfDay(minDate))
    )
      return true;
    if (
      maxDate &&
      !isSameDay(dayStart, startOfDay(maxDate)) &&
      isAfter(dayStart, startOfDay(maxDate))
    )
      return true;

    return disabledDates.some((disabledDate) =>
      isSameDay(dayStart, startOfDay(disabledDate)),
    );
  };

  const isDateWaitlistOnly = (date: Date): boolean => {
    const dayStart = startOfDay(date);
    return waitlistOnlyDates.some((waitlistDate) =>
      isSameDay(dayStart, startOfDay(waitlistDate)),
    );
  };

  const handleDateSelect = (date: Date) => {
    /* istanbul ignore else - CalendarDay already prevents onPress for disabled dates */
    if (!isDateDisabled(date)) {
      onDateSelect?.(date);
    }
  };

  const handleTodayPress = () => {
    onDateSelect?.(new Date());
  };

  return (
    <Box {...viewProps} testID={testID} style={styles.container}>
      <View style={styles.navigationContainer}>
        <View style={styles.monthTitles}>
          {months
            .slice(scrolledMonthIndex, scrolledMonthIndex + monthsPerPage)
            .map((month, index) => (
              <View
                key={month.toString()}
                style={
                  index < monthsPerPage - 1
                    ? styles.leadingMonthTitle(monthStride)
                    : undefined
                }
              >
                <Text variant="lStrong" style={styles.monthText}>
                  {formatDate(month, DateFormat.FULL_MONTH_YEAR, i18n.language)}
                </Text>
              </View>
            ))}
        </View>
        <View style={styles.navigationButtonsContainer}>
          <Button
            size="small"
            variant="secondary"
            onPress={handleTodayPress}
            style={styles.todayButton}
          >
            <Text variant="s">{t('today')}</Text>
          </Button>
          <Button
            size="small"
            variant="secondary"
            onPress={handlePreviousMonth}
            disabled={scrolledMonthIndex === 0}
            style={styles.arrowButton}
            accessibilityLabel={t('accessibility.previousMonth')}
          >
            <Icon name="chevronLeft" size={16} />
          </Button>
          <Button
            size="small"
            variant="secondary"
            onPress={handleNextMonth}
            disabled={scrolledMonthIndex >= lastPageIndex}
            style={styles.arrowButton}
            accessibilityLabel={t('accessibility.nextMonth')}
          >
            <Icon name="chevronRight" size={16} />
          </Button>
        </View>
      </View>
      <AnimatedFlatList
        ref={scrollViewRef}
        horizontal
        pagingEnabled
        decelerationRate="fast"
        showsHorizontalScrollIndicator={false}
        onScroll={scrollHandler}
        onLayout={handleListLayout}
        scrollEventThrottle={16}
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContainerContent(
          monthGap,
          trailingPadding,
        )}
        testID={`${testID}-scroll-view`}
        data={months}
        getItemLayout={(_, index) => ({
          length: monthWidth,
          offset: monthStride * index,
          index,
        })}
        keyExtractor={(item) => (item as Date).toString()}
        renderItem={({ item }) => (
          <CalendarMonth
            month={item as Date}
            testID={`${testID}-month-${formatDate(item as Date, DateFormat.MONTH_YEAR)}`}
            width={monthWidth}
            isDateDisabled={isDateDisabled}
            isDateWaitlistOnly={isDateWaitlistOnly}
            handleDateSelect={handleDateSelect}
            minDate={minDate}
            maxDate={maxDate}
            selectedDate={selectedDate}
          />
        )}
      />
    </Box>
  );
};

Calendar.displayName = 'Calendar';
export { Calendar };
